import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  useGetWarrantyPeriodsQuery,
  useCreateWarrantyPeriodMutation,
  useUpdateWarrantyPeriodMutation,
  useDeleteWarrantyPeriodMutation,
} from '../../../store/api/productsApi';

export default function WarrantyPeriods() {
  const { data: warrantyPeriods, isLoading, error } = useGetWarrantyPeriodsQuery();
  const [createWarrantyPeriod] = useCreateWarrantyPeriodMutation();
  const [updateWarrantyPeriod] = useUpdateWarrantyPeriodMutation();
  const [deleteWarrantyPeriod] = useDeleteWarrantyPeriodMutation();

  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    durationMonths: '',
    description: '',
    isActive: true,
  });

  const handleOpen = (item = null) => {
    if (item) {
      setEditItem(item);
      setFormData({
        name: item.name,
        durationMonths: item.durationMonths,
        description: item.description || '',
        isActive: item.isActive,
      });
    } else {
      setEditItem(null);
      setFormData({
        name: '',
        durationMonths: '',
        description: '',
        isActive: true,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditItem(null);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) {
        await updateWarrantyPeriod({ id: editItem.id, ...formData }).unwrap();
      } else {
        await createWarrantyPeriod(formData).unwrap();
      }
      handleClose();
    } catch (err) {
      alert(err.data?.message || 'An error occurred');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this warranty period?')) {
      try {
        await deleteWarrantyPeriod(id).unwrap();
      } catch (err) {
        alert(err.data?.message || 'Error deleting warranty period');
      }
    }
  };

  if (isLoading) return <CircularProgress />;
  if (error) return <Alert severity="error">Error loading warranty periods</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Warranty Periods</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Warranty Period
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Duration (Months)</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {warrantyPeriods?.map((period) => (
              <TableRow key={period.id}>
                <TableCell>{period.name}</TableCell>
                <TableCell>{period.durationMonths}</TableCell>
                <TableCell>{period.description}</TableCell>
                <TableCell>{period.isActive ? 'Active' : 'Inactive'}</TableCell>
                <TableCell align="right">
                  <IconButton color="primary" onClick={() => handleOpen(period)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDelete(period.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {(!warrantyPeriods || warrantyPeriods.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No warranty periods found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? 'Edit Warranty Period' : 'Add Warranty Period'}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name (e.g. 1 Year)"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Duration in Months"
              name="durationMonths"
              type="number"
              value={formData.durationMonths}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleChange}
                  name="isActive"
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
